import qs from 'qs';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { CONSOLIDATED_METHODS, LASSEN_LOCATIONS } from '../../constants/endpoints';
import {
  CARRIERS_WITH_FEATURE_SORT,
  STORE_LOCATOR_DEFAULT_PARAMS,
  STORE_SEARCH_LIMIT,
} from '../../constants/storeLocator';
import { updateStep2Complete } from '../page';
import { BOXLESS, DROP_OFF } from '../../constants/returnMethods';
import generateMethodKey from '../../lib/generateMethodKey';
import { RECEIVE_AND_SELECT_RETURN_OPTION_METHOD } from '../returnOptions';
import { mapCostsFromWholeToDecimalUnits } from '../../lib/currencyFormatters';
import { SELECT_RETURN_METHOD } from '../returnMethods';
import { getAllFees, getFeeTag } from '../../lib/consolidatedMethods';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { getUrlInfo } from '../../lib/url';

export const RECEIVE_LOCATIONS = 'RECEIVE_LOCATIONS';
export const RECEIVE_CONSOLIDATED_LOCATIONS = 'RECEIVE_CONSOLIDATED_LOCATIONS';
export const RECEIVE_STORE_LOCATOR_GEOLOCATION = 'RECEIVE_STORE_LOCATOR_GEOLOCATION';
export const SET_SELECTED_LOCATION = 'SET_SELECTED_LOCATION';
export const SET_SEARCH_VIEW = 'SET_SEARCH_VIEW';
export const SET_IS_CONSOLIDATED = 'SET_IS_CONSOLIDATED';
export const SET_IS_FETCHING_LOCATIONS = 'SET_IS_FETCHING_LOCATIONS';
export const RECEIVE_STORE_LOCATOR_SELECTION_REQUIRED = 'RECEIVE_STORE_LOCATOR_SELECTION_REQUIRED';
export const RECEIVE_SORTED_BY = 'RECEIVE_SORTED_BY';
export const RECEIVE_LOCATION_TAG_SETTINGS = 'RECEIVE_LOCATION_TAG_SETTINGS';
export const RESET_SELECTED_LOCATION = 'RESET_SELECTED_LOCATION';

export const receiveLocationTagSettings = (locationTagSettings) => ({
  type: RECEIVE_LOCATION_TAG_SETTINGS,
  data: {
    locationTagSettings,
  },
});

export const setIsFetchingLocations = (isFetchingLocations) => ({
  type: SET_IS_FETCHING_LOCATIONS,
  data: {
    isFetchingLocations,
  },
});

export const setSearchView = (isSearchView) => ({
  type: SET_SEARCH_VIEW,
  data: {
    isSearchView,
  },
});

export const resetSelectedLocation = () => ({
  type: RESET_SELECTED_LOCATION,
});

export const generateParams = (methodKey) => (_dispatch, getState) => {
  const {
    returnOptions: {
      returnOptionMethods: {
        [methodKey]: {
          id,
          carrier,
          store_locator_radius: radius = 10,
          store_locator_query_params: queryParams,
        } = {},
      } = {},
    } = {},
    storeLocator: { country, postalCode } = {},
    page: { languageCode: language } = {},
  } = getState();

  const baseParams = {
    country,
    postalCode,
    radius,
    limit: [STORE_SEARCH_LIMIT],
    resourceTypes: ['servicecenter'],
    language,
  };

  const paramList =
    queryParams || STORE_LOCATOR_DEFAULT_PARAMS[methodKey] || STORE_LOCATOR_DEFAULT_PARAMS[id];

  let methodParams = { carriers: carrier };
  if (paramList) {
    const transformedParams = paramList.reduce(
      (accumParams, { key, value }) => ({
        ...accumParams,
        [key]: value,
      }),
      {},
    );
    const ensureDefault =
      STORE_LOCATOR_DEFAULT_PARAMS[methodKey] || STORE_LOCATOR_DEFAULT_PARAMS[id] || [];
    ensureDefault.forEach(({ key, value }) => {
      if (!transformedParams[key]) transformedParams[key] = value;
    });

    methodParams = transformedParams;
  }

  const mergedParams = { ...baseParams, ...methodParams };

  const carrierUsesFeaturedSort =
    mergedParams.carriers &&
    mergedParams.carriers.some((carrierMoniker) =>
      CARRIERS_WITH_FEATURE_SORT.includes(carrierMoniker.toLowerCase()),
    );

  if (carrierUsesFeaturedSort && mergedParams.supportedactions !== BOXLESS) {
    mergedParams.featuredsort = true;
  }

  return mergedParams;
};

export const queryLocations = (methodKey) => async (dispatch, getState) => {
  const {
    page: { productType } = {},
    storeLocator: {
      postalCode,
      cachedQueries: { [methodKey]: { [postalCode]: cachedLocationIds } = {} } = {},
    } = {},
    returnsSettings: { [productType]: { returnOptions: { authToken } = {} } = {} } = {},
  } = getState();

  if (cachedLocationIds) return;

  const params = await dispatch(generateParams(methodKey));

  try {
    const response = await fetch(
      LASSEN_LOCATIONS(qs.stringify({ ...params }, { indices: false })),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Authentication': authToken,
        },
      },
    );

    const { locations = [] } = await response.json();

    await dispatch({
      type: RECEIVE_LOCATIONS,
      data: {
        postalCode,
        methodKey,
        locationIds: locations.map((location) => location.location_id),
        locationsById: keyBy(locations, 'location_id'),
      },
    });
  } catch (error) {
    console.error(error);
  }
};

export const receiveConsolidatedLocations = ({ postalCode, option, locations }) => ({
  type: RECEIVE_CONSOLIDATED_LOCATIONS,
  data: {
    postalCode,
    optionId: option.id,
    locations,
  },
});

export const fetchStoreLocations = () => async (dispatch, getState) => {
  try {
    const {
      returnOptions: { returnOptionMethods },
    } = getState();
    dispatch(setIsFetchingLocations(true));

    const storeLocatorMethods = Object.keys(returnOptionMethods).reduce(
      (filteredMethodKeys, methodKey) => {
        if (returnOptionMethods[methodKey].store_locator) {
          return [...filteredMethodKeys, methodKey];
        }
        return filteredMethodKeys;
      },
      [],
    );

    await Promise.all(storeLocatorMethods.map((methodKey) => dispatch(queryLocations(methodKey))));
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(setIsFetchingLocations(false));
  }
};

export const setSelectedLocation = (selectedLocation) => (dispatch, getState) => {
  const {
    page: { productType } = {},
    storeLocator: { isConsolidated, selectionRequired } = {},
    returnOptions: { returnOptionMethods, selectedOptionId } = {},
    returnsSettings: {
      [productType]: { returnOptions: { methods: settingsMethods } = {} } = {},
    } = {},
  } = getState();

  if (selectionRequired && !isEmpty(selectedLocation)) {
    dispatch(updateStep2Complete(true));
  }

  if (isConsolidated) {
    const {
      return_info: {
        preferred_method_carrier: preferredMethodCarrier = {},
        return_cost: locationReturnCost,
      } = {},
    } = selectedLocation;
    const { [selectedOptionId]: { return_cost: optionReturnCost } = {} } = returnOptionMethods;
    const selectedOptionMethodKey = generateMethodKey(preferredMethodCarrier);
    const returnOptionMethod = {
      ...(returnOptionMethods[selectedOptionMethodKey] || settingsMethods[selectedOptionMethodKey]),
      ...preferredMethodCarrier,
      id: preferredMethodCarrier.return_method,
      return_cost: locationReturnCost
        ? mapCostsFromWholeToDecimalUnits(locationReturnCost)
        : optionReturnCost,
    };
    dispatch({
      type: RECEIVE_AND_SELECT_RETURN_OPTION_METHOD,
      data: {
        selectedOptionMethodKey,
        returnOptionMethod,
      },
    });
    dispatch({
      type: SELECT_RETURN_METHOD,
      data: {
        selectedMethod: returnOptionMethod,
        selectedMethodKey: selectedOptionMethodKey,
      },
    });
  }

  return dispatch({
    type: SET_SELECTED_LOCATION,
    data: {
      selectedLocation,
    },
  });
};

export const receiveStoreLocatorGeolocation =
  ({ postalCode, country }) =>
  (dispatch, getState) => {
    const { storeLocator: { country: storeLocatorCountry, selectionRequired } = {} } = getState();

    if (selectionRequired) {
      dispatch(updateStep2Complete(false));
    }

    return dispatch({
      type: RECEIVE_STORE_LOCATOR_GEOLOCATION,
      data: {
        postalCode,
        country: (country || storeLocatorCountry || '').toUpperCase(),
      },
    });
  };

export const receiveSelectionRequired = (selectionRequired) => ({
  type: RECEIVE_STORE_LOCATOR_SELECTION_REQUIRED,
  data: {
    selectionRequired,
  },
});

export const refetchConsolidatedLocations =
  (postalCode, selectedOptionId) => async (dispatch, getState) => {
    try {
      dispatch(setIsFetchingLocations(true));
      dispatch(updateStep2Complete(false));

      const {
        language,
        page: { locale, giftOrder = false, browserFingerprint, overrideCode, languageCode } = {},
        retailerSettings: { name: retailerName } = {},
        storeLocator: { country, locationTagSettings } = {},
        orderInfo: { returnOrder } = {},
      } = getState();
      const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY;

      const { params: { preview, version } = {} } = getUrlInfo();

      const response = await fetch(
        CONSOLIDATED_METHODS({
          retailerName,
          params: qs.stringify({
            locale,
            ...(preview === 'true' ? { preview, settings_version: version } : {}),
          }),
        }),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'narvar-tfid': browserFingerprint,
          },
          body: JSON.stringify({
            order_number: returnOrder.order_number,
            return_items: returnOrder.return_items,
            postal_code: postalCode,
            country,
            return_override_code: overrideCode,
            gift: giftOrder,
          }),
        },
      );

      const { location_based_pudo_options: pudoOptions } = await response.json();

      pudoOptions.forEach((option) => {
        const { shippingFees } = getAllFees(option);
        dispatch(
          receiveConsolidatedLocations({
            postalCode,
            option: { id: option.display_method || option.return_method },
            locations: option.locations.map((location) => {
              const {
                id,
                address,
                logo_url: logoUrl,
                additional_details: additionalDetails,
                return_info: {
                  return_cost: returnCost,
                  preferred_method_carrier: { return_method: returnMethod } = {},
                } = {},
              } = location;
              const businessInfo = (additionalDetails && additionalDetails.business_info) || [];
              const methodInstructions =
                DEFAULT_LANG_COPY[`returns.return_options.locations.${returnMethod}.instructions`];
              return {
                ...location,
                ...(address
                  ? {
                      address: {
                        address_line1: address.address1,
                        address_line2: address.address2,
                        city: address.city,
                        country: address.country,
                        country_code: address.country_code,
                        postal_code: address.postal_code,
                        region: address.region,
                        region_code: address.region_code,
                      },
                      store_address: {
                        address_line1: address.address1,
                        address_line2: address.address2,
                        city: address.city,
                        country: address.country,
                        country_code: address.country_code,
                        postal_code: address.postal_code,
                        region: address.region,
                        region_code: address.region_code,
                      },
                    }
                  : undefined),
                logos: {
                  square_1x: { url: logoUrl },
                  square_2x: { url: logoUrl },
                },
                location_id: id,
                fee:
                  shippingFees.length > 1
                    ? getFeeTag({
                        option: {
                          ...option,
                          return_cost: mapCostsFromWholeToDecimalUnits(option.return_cost),
                        },
                        location: {
                          ...location,
                          return_info: {
                            ...location.return_info,
                            return_cost: returnCost
                              ? mapCostsFromWholeToDecimalUnits(returnCost)
                              : returnCost,
                          },
                        },
                        language,
                        locale,
                      })
                    : undefined,
                ...(option.display_method === DROP_OFF
                  ? {
                      tag: {
                        style: locationTagSettings.methods.style,
                        value: locationTagSettings.methods.content[returnMethod],
                      },
                      additional_details: address
                        ? {
                            ...(additionalDetails || {}),
                            business_info: [
                              ...businessInfo,
                              ...(methodInstructions
                                ? [
                                    {
                                      icon: 'info',
                                      value: methodInstructions,
                                    },
                                  ]
                                : []),
                            ],
                          }
                        : undefined,
                    }
                  : {}),
              };
            }),
          }),
        );
      });
    } catch (error) {
      console.error(error);
      dispatch(
        receiveConsolidatedLocations({
          postalCode,
          option: { id: selectedOptionId },
          locations: [],
        }),
      );
    }

    dispatch(receiveStoreLocatorGeolocation({ postalCode }));
    dispatch(setIsFetchingLocations(false));
  };
